import React, { useState } from "react";
import {
  Headphones,
  Phone,
  Copy,
  Check,
  X,
  ExternalLink,
  ShieldCheck,
  MessageSquare,
  Clock,
  User,
} from "lucide-react";
import { toast } from "sonner";

export const SUPPORT_INFO = {
  name: "Kunal Warkante",
  phone: "9545262332",
  company: "JSM DIGITAL SOLUTIONS",
  email: "jsmdigitalsolution@gmail.com",
  hours: "9:00 AM – 9:00 PM (Monday – Sunday)",
};

export default function CustomerSupportModal({ isOpen, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(SUPPORT_INFO.phone);
    setCopied(true);
    toast.success("Support phone number copied: " + SUPPORT_INFO.phone);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-transparent border-b border-zinc-800 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <Headphones className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-1.5">
                <span>Print</span>
                <span className="text-amber-400">Q</span>
                <span>Customer Support</span>
              </h2>
              <p className="text-xs text-zinc-400">Direct assistance from JSM DIGITAL SOLUTIONS</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col gap-4">
          {/* Executive Card */}
          <div className="bg-zinc-950/80 border border-zinc-800/90 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-300">
                  <User className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{SUPPORT_INFO.name}</div>
                  <div className="text-[11px] text-amber-400/90 font-medium">Customer Support & Technical Lead</div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Available
              </span>
            </div>

            {/* Direct Phone Highlight */}
            <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-lg p-3">
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-amber-400" />
                <span className="font-mono text-base font-bold text-white tracking-wide">
                  +91 {SUPPORT_INFO.phone}
                </span>
              </div>
              <button
                onClick={handleCopy}
                className="px-2.5 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-200 flex items-center gap-1 transition-colors"
                title="Copy Number"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? "Copied" : "Copy"}</span>
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <a
              href={`tel:${SUPPORT_INFO.phone}`}
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 text-zinc-950 font-bold text-xs shadow-md shadow-amber-500/20 transition-all active:scale-95 text-center"
            >
              <Phone className="w-4 h-4" />
              <span>Call Support</span>
            </a>
            <button
              onClick={handleCopy}
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-semibold text-xs transition-all active:scale-95"
            >
              <Copy className="w-4 h-4 text-zinc-400" />
              <span>Copy Number</span>
            </button>
          </div>

          {/* Support Notes */}
          <div className="bg-zinc-950/40 rounded-xl p-3.5 border border-zinc-800/60 flex flex-col gap-2 text-[11px] text-zinc-400">
            <div className="flex items-center gap-2 text-zinc-300 font-semibold">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span>Support Hours: {SUPPORT_INFO.hours}</span>
            </div>
            <p className="leading-relaxed text-zinc-400">
              For device license unbinding, PC changes, renewal questions, or printer configuration help, feel free to call our support directly.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-zinc-950 border-t border-zinc-800 px-5 py-3 flex items-center justify-between text-[11px] text-zinc-500">
          <span>{SUPPORT_INFO.company}</span>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
